"use client";

import { atom, useAtom } from "jotai";
import { useCallback, useTransition } from "react";
import { flushSync } from "react-dom";

interface Data {
	title: string;
	description?: string;
	confirmText?: string;
	cancelText?: string;
	onConfirm?: () => void | Promise<void>;
	pendingOnConfirm?: boolean;
}

type Message = {
	resolve: (value: boolean) => void;
	promise: Promise<boolean>;
} & Data;

const confirmAtom = atom<Message | undefined>();

export const useConfirm = () => {
	const [pending, startTransition] = useTransition();
	const [state, setState] = useAtom(confirmAtom);

	const ask = (data: Data) => {
		// Clear previous state
		if (state) {
			state.resolve(false);
			flushSync(() => {
				setState(undefined);
			});
		}

		const { promise, resolve } = promiseWithResolvers<boolean>();
		flushSync(() => {
			setState({
				...data,
				promise,
				resolve,
			});
		});

		return promise;
	};

	const cancel = useCallback(() => {
		if (state) {
			state.resolve(false);
			setState(undefined);
		}
	}, [state, setState]);

	const confirm = useCallback(() => {
		if (state) {
			state.resolve(true);
			if (state.onConfirm) {
				if (state.pendingOnConfirm) {
					startTransition(async () => {
						if (!state.onConfirm) return;
						await state.onConfirm();
						setState(undefined);
					});
				} else {
					state.onConfirm();
					setState(undefined);
					return;
				}
			} else {
				setState(undefined);
			}
		}
	}, [state, setState]);

	return {
		ask,
		open: !!state,
		cancel,
		state,
		confirm,
		pending,
	};
};

const promiseWithResolvers = <T>() => {
	let resolve!: (value: T) => void;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	let reject!: (reason?: any) => void;
	const promise = new Promise<T>((res, rej) => {
		resolve = res;
		reject = rej;
	});
	return {
		promise,
		resolve,
		reject,
	};
};
